// src/pages/dashboard/Dashboard.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  CreditCard,
  BellRing,
  ShieldCheck,
  LogOut,
  Menu,
  X,
  ChevronRight,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectCurrentUser,
  selectIsAuthenticated,
  logout as logoutAction,
} from "../../app/features/authSlice";
import {
  useGetCurrentUserQuery,
  useLogoutMutation,
} from "../../app/api/authApi";

const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;
const LAST_ACTIVE_KEY = "auth_last_active_at";

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function setLastActiveNow() {
  localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
}
function getLastActive(): number | null {
  const v = localStorage.getItem(LAST_ACTIVE_KEY);
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function clearLastActive() {
  localStorage.removeItem(LAST_ACTIVE_KEY);
}

const DashboardLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const user = useAppSelector(selectCurrentUser);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const token = useAppSelector((s: any) => s.auth.token as string | null);

  // Sidebar on mobile
  const [mobileOpen, setMobileOpen] = useState(false);

  // --- Persist session/user across refresh + 12h inactivity ---
  // If token exists, fetch /auth/me to hydrate user on refresh.
  const shouldFetchMe = Boolean(token);
  const { isFetching } = useGetCurrentUserQuery(undefined, {
    skip: !shouldFetchMe,
  });

  const [apiLogout, { isLoading: isLoggingOut }] = useLogoutMutation();

  useEffect(() => {
    // Update last activity on common user interactions
    const mark = () => setLastActiveNow();

    const events: Array<keyof WindowEventMap> = [
      "click",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
    ];
    events.forEach((e) => window.addEventListener(e, mark, { passive: true }));

    // Initialize last active if missing and token exists
    if (token && !getLastActive()) setLastActiveNow();

    const interval = window.setInterval(() => {
      if (!token) return;

      const last = getLastActive();
      if (!last) {
        setLastActiveNow();
        return;
      }

      const inactiveFor = Date.now() - last;
      if (inactiveFor >= TWELVE_HOURS_MS) {
        // 12 hours inactivity -> logout locally (and optionally API logout)
        dispatch(logoutAction());
        clearLastActive();
        navigate("/sign-in", { replace: true });
      }
    }, 30_000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, mark as any));
      window.clearInterval(interval);
    };
  }, [dispatch, navigate, token]);

  useEffect(() => {
    // If user is not authenticated and no token, push to login.
    // (If token exists, /me will set authenticated in slice)
    if (!token && !isAuthenticated) {
      navigate("/sign-in", { replace: true, state: { from: location } });
    }
  }, [token, isAuthenticated, navigate, location]);

  const navItems = useMemo(
    () => [
      { to: "/user/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
      { to: "/user/contacts", label: "Contacts", icon: Users },
      { to: "/user/payments", label: "Payments", icon: CreditCard },
      { to: "/user/follow-ups", label: "Follow-ups", icon: BellRing },
      { to: "/user/users", label: "Team & Users", icon: ShieldCheck },
    ],
    [],
  );

  const handleLogout = async () => {
    try {
      // best-effort API logout
      await apiLogout().unwrap();
    } catch {
      // ignore
    } finally {
      dispatch(logoutAction());
      clearLastActive();
      navigate("/sign-in", { replace: true });
    }
  };

  const activeClass = "bg-slate-900 text-white shadow-sm";
  const inactiveClass = "text-slate-700 hover:bg-slate-100";

  return (
    <div className="min-h-[calc(100vh-64px)] bg-slate-50">
      {/* Top bar */}
      <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen((s) => !s)}
              className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white p-2 text-slate-700 shadow-sm hover:bg-slate-50 md:hidden"
              aria-label="Toggle menu"
            >
              {mobileOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>

            <Link to="/user/dashboard" className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-xl bg-slate-900" />
              <div className="leading-tight">
                <p className="text-sm font-semibold text-slate-900">
                  Dashboard
                </p>
                <p className="text-xs text-slate-500">
                  {isFetching
                    ? "Loading..."
                    : user
                      ? `${user.firstName} ${user.lastName}`
                      : "Welcome"}
                </p>
              </div>
            </Link>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            disabled={isLoggingOut}
            className={cn(
              "inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold shadow-sm transition",
              isLoggingOut
                ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
            )}
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-6 md:grid-cols-[260px_1fr]">
        {/* Mobile backdrop — tap to close the floating menu */}
        {mobileOpen && (
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
            className="fixed inset-0 z-30 bg-slate-900/30 backdrop-blur-sm md:hidden"
          />
        )}

        {/* Sidebar */}
        <aside
          className={cn(
            "rounded-2xl border border-slate-200 bg-white p-3 shadow-sm md:block",
            // Desktop: sticky column that stays in view while the main content scrolls.
            "md:sticky md:top-20 md:self-start md:z-auto md:max-h-[calc(100vh-6rem)] md:overflow-y-auto",
            // Mobile: float over the content instead of pushing it down.
            mobileOpen
              ? "fixed inset-x-4 top-[76px] z-40 max-h-[calc(100vh-92px)] overflow-y-auto md:inset-x-auto"
              : "hidden md:block",
          )}
        >
          <nav className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={(item as any).end}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold transition",
                      isActive ? activeClass : inactiveClass,
                    )
                  }
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.label}</span>
                  <ChevronRight className="ml-auto h-4 w-4 opacity-40" />
                </NavLink>
              );
            })}
          </nav>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-900">Quick tip</p>
            <p className="mt-1 text-xs text-slate-600">
              Your session stays active while you use the app. After 12 hours of
              inactivity, you’ll be logged out automatically.
            </p>
          </div>
        </aside>

        {/* Main content */}
        <main className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          {/* Outlet renders nested dashboard pages */}
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
